import { useEffect, type ReactNode } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import RouteLoading from '../RouteLoading';
import { workflowService } from '../../services/workflow.services';
import type { Workflow } from '../../types/workflow';
import authStore from '../../store/auth';
import { REPORT_TYPES } from '../../consts/reportTypes';
import { observer } from 'mobx-react-lite';

interface WorkflowRouteGuardProps {
  children: ReactNode;
  allowedReportTypes: readonly string[];
}

interface WorkflowRequestError {
  status?: number;
  response?: {
    status?: number;
  };
}

type GuardTarget = Pick<Workflow, 'report_type'> & {
  workflows?: Array<Pick<Workflow, 'report_type'>>;
};
type WorkflowResponse = GuardTarget | { data?: GuardTarget };

const getWorkflow = (response?: WorkflowResponse): GuardTarget | undefined => {
  const wrapped = response as { data?: GuardTarget } | undefined;
  return wrapped?.data ?? (response as GuardTarget | undefined);
};

const WorkflowRouteGuard = ({ children, allowedReportTypes }: WorkflowRouteGuardProps) => {
  const { id, projectId, workflowId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const grouped =
    params.get('type') === 'grouped' ||
    params.get('reportType') === 'grouped' ||
    params.has('jurisdictionId');
  const targetWorkflowId = workflowId || projectId || id;
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<
    WorkflowResponse,
    WorkflowRequestError
  >(
    [
      'WORKFLOW_ACCESS_GUARD',
      localStorage.getItem('activeCompanyId'),
      (authStore.user as { localAccountId?: string } | null)?.localAccountId,
      grouped,
      targetWorkflowId
    ],
    () =>
      (grouped
        ? workflowService.getGroupedWorkflows(targetWorkflowId)
        : workflowService.getWorkflowById(
            targetWorkflowId
          )) as unknown as Promise<WorkflowResponse>,
    {
      cacheTime: 5 * 60 * 1000,
      enabled: !!targetWorkflowId,
      retry: false,
      staleTime: 0
    }
  );

  const workflow = getWorkflow(data);
  const isAccessDenied =
    isError && [401, 403, 404].includes(error?.response?.status ?? error?.status ?? 0);
  const types = grouped
    ? workflow?.workflows?.map(item => item.report_type)
    : workflow
      ? [workflow.report_type]
      : [];
  const isProductMismatch =
    !!targetWorkflowId &&
    !!allowedReportTypes &&
    !!workflow &&
    !!types?.length &&
    types.some(
      type => !allowedReportTypes.includes(type) || (grouped && type === REPORT_TYPES.PRIIPS)
    );
  const shouldRedirect = isAccessDenied || isProductMismatch;

  useEffect(() => {
    if (!shouldRedirect) return;

    toast.error(
      isProductMismatch
        ? 'This page is not available for that product.'
        : 'You do not have access to that project.'
    );
    navigate('/project', { replace: true });
  }, [isProductMismatch, navigate, shouldRedirect]);

  if (isLoading) return <RouteLoading />;
  if (shouldRedirect) return null;
  if (!targetWorkflowId || isError || !types?.length || types.some(type => !type))
    return (
      <section id="workflow-access-error" role="alert">
        <p id="workflow-access-error-message">
          {targetWorkflowId
            ? 'Unable to verify access to this project. No report has been opened.'
            : 'A project is required to open this page.'}
        </p>
        {targetWorkflowId && (
          <button
            id="workflow-access-retry"
            type="button"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            Retry access check
          </button>
        )}
      </section>
    );

  return children;
};

export default observer(WorkflowRouteGuard);
